
import { Injectable } from '@angular/core';
import { filter, first, map, switchMap, tap, take, auditTime, distinctUntilChanged, delay, timeout, catchError } from 'rxjs/operators';
import { ServerResponse } from '@app/enums/server-response';
import { select, Store } from '@ngrx/store';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { LobbyService } from '../../../features/lobby/services/lobby.service';
// import { LobbyNav } from '../../enums/lobby-nav';
// import * as LobbyActions from '../actions/lobby.actions';
import * as SitNGoSummariesActions from './sit-n-go-summaries.actions';
import { WsService } from '@app/services/ws.service';
import { SitNGoSummary, SitNGoSummaryDTO } from '@app/models/sit-n-go-summary';
import { LiveLobbyTournament } from '@app/models/live-lobby-tournament';
import { combineLatest, forkJoin, of, EMPTY } from 'rxjs';
import * as SitNGoSummariesSelector from './sit-n-go-summaries.selector';
import { isEqual } from 'lodash';
import { TournamentInformationResponse } from '@app/models/tournament-information';
import * as UserSelectors from '@app/store/features/user/user.selectors';

@Injectable()
export class SitNGoSummariesEffects {
    constructor(
        private _ws: WsService,
        private readonly store: Store,
        private readonly lobbyService: LobbyService,
        private actions$: Actions,

    ) { }

    // updateActiveNav$ = createEffect(
    //     () =>
    //         this.actions$.pipe(
    //             ofType(LobbyActions.updateActiveNav),
    //             filter(({ activeNav }) => activeNav === LobbyNav.SitNGo),
    //             tap(() => this.lobbyService.getSitNGoSummaries()),
    //             map(() => SitNGoSummariesActions.updateLoader({ loader: true }))
    //         ));


    updateSitNGoSummaries$ = createEffect(() =>
        this._ws.getDataResponse<SitNGoSummaryDTO[]>(ServerResponse.SitNGoSummaries)
            .pipe(
                map(sitNGoSummariesResponse => {
                    const sitNGoSummaries: SitNGoSummary[] = sitNGoSummariesResponse.map(sitNGoSummaryDTO => {
                        return this.lobbyService.updateSitNGoSummary(sitNGoSummaryDTO)
                    })

                    return SitNGoSummariesActions.updateAll({ sitNGoSummaries })
                })
            ));


    liveLobbyTournament$ = createEffect(() =>
        this._ws.getDataResponse<LiveLobbyTournament>(ServerResponse.LiveLobbyTournament)
            .pipe(
                // 🔧 FIX: Use switchMap to cancel previous inner subscriptions
                switchMap(liveLobbyTournament =>
                    this.store.pipe(
                        select(SitNGoSummariesSelector.selectEntityById(liveLobbyTournament.idTournament)),
                        filter(tourSum => !!tourSum),
                        take(1), // 🔧 FIX: Complete inner subscription after first valid value
                        timeout(5000), // 🔧 FIX: Prevent hanging if entity doesn't exist
                        map(sitNGoSummary => ({ liveLobbyTournament, sitNGoSummary })),
                        catchError(() => EMPTY) // 🔧 FIX: Skip if entity not found
                    )
                ),
                map(({ liveLobbyTournament, sitNGoSummary }) => {
                    // Immutable update - no mutation
                    const data: SitNGoSummary = {
                        ...sitNGoSummary!,
                        status: liveLobbyTournament.status,
                        status2: liveLobbyTournament.status2,
                        nbPlayerRegistered: liveLobbyTournament.nbPlayersRegistered,
                        nbPlayersActive: liveLobbyTournament.nbPlayersActive,
                        blind: liveLobbyTournament.blind,
                        startIn: liveLobbyTournament.startIn
                    };

                    return SitNGoSummariesActions.updateOne({ sitNGoSummary: data })
                }),
                catchError((error) => {
                    console.warn('SitNGo liveLobbyTournament$ error:', error);
                    return EMPTY;
                })
            )
    );


    updateTournamentInformationResponse$ = createEffect(() =>
        this._ws.getDataResponse<TournamentInformationResponse>(ServerResponse.TournamentInformation)
            .pipe(
                delay(200), //wait for tournament info to be processed
                auditTime(120), //collapse bursts: keep only the last event every 120ms
                distinctUntilChanged((a, b) => isEqual(a, b)),

                // 🔧 FIX: Use switchMap to cancel previous inner subscriptions
                switchMap((tournamentInformationResponse) => {
                    return combineLatest([
                        of(tournamentInformationResponse),
                        this.store.pipe(select(UserSelectors.selectUserProfile), take(1)),
                        this.store.pipe(
                            select(SitNGoSummariesSelector.selectEntityById(tournamentInformationResponse.idTournament)),
                            filter(data => !!data),
                            map(data => data!),
                            take(1), // 🔧 FIX: Complete inner subscription
                            timeout(5000), // 🔧 FIX: Prevent hanging
                            catchError(() => EMPTY) // 🔧 FIX: Handle missing entity
                        )
                    ])
                }),
                filter(([_, __, sitNGoSummary]) => !!sitNGoSummary), // 🔧 FIX: Skip if not found
                map(([tournamentInformationResponse, userProfile, sitNGoSummary]) => {
                    const myPlayer = tournamentInformationResponse.players.find(player => player.idPlayer === userProfile?.id);
                    const imOnTour = !myPlayer?.positionEnded ? myPlayer : undefined;
                    const iWasOnTour = myPlayer?.positionEnded ? myPlayer : undefined;
                    const isRegistered = !!(imOnTour && !imOnTour.positionEnded);

                    // Immutable update - no mutation
                    const tourSumGame = {
                        ...sitNGoSummary.game,
                        imOnTour: !!imOnTour,
                        iWasOnTour: !!iWasOnTour,
                        isRegistered: isRegistered
                    };

                    return SitNGoSummariesActions.updateOne({
                        sitNGoSummary: {
                            ...sitNGoSummary,
                            nbPlayerRegistered: tournamentInformationResponse.nbPlayerRegistered,
                            nbRebuyUsed: tournamentInformationResponse.nbRebuyUsed,
                            nbAddonUsed: tournamentInformationResponse.nbAddonUsed,
                            totalPrizePool: tournamentInformationResponse.totalPrizePool,
                            imRegistered: isRegistered,
                            game: tourSumGame
                        }
                    })
                }),
                catchError((error) => {
                    console.warn('SitNGo updateTournamentInformationResponse$ error:', error);
                    return EMPTY;
                })
            )
    );
} 